import { colors } from '../config/colors';

/**
 * Country to broad region mapping for company location display.
 * Extend as needed for your data.
 */
const COUNTRY_TO_REGION = {
  China: 'Asia Pacific',
  Japan: 'Asia Pacific',
  India: 'Asia Pacific',
  'South Korea': 'Asia Pacific',
  Australia: 'Asia Pacific',
  Indonesia: 'Asia Pacific',
  Thailand: 'Asia Pacific',
  Vietnam: 'Asia Pacific',
  Malaysia: 'Asia Pacific',
  Singapore: 'Asia Pacific',
  Philippines: 'Asia Pacific',
  'United States': 'Americas',
  'United States of America': 'Americas',
  Canada: 'Americas',
  Mexico: 'Americas',
  Brazil: 'Americas',
  Germany: 'Europe',
  'United Kingdom': 'Europe',
  France: 'Europe',
  Italy: 'Europe',
  Spain: 'Europe',
  Netherlands: 'Europe',
  Poland: 'Europe',
  Belgium: 'Europe',
  Switzerland: 'Europe',
  Sweden: 'Europe',
  Norway: 'Europe',
  Russia: 'Europe',
  'Saudi Arabia': 'Middle East',
  'United Arab Emirates': 'Middle East',
  Israel: 'Middle East',
  Turkey: 'Europe',
  'South Africa': 'Africa',
  Nigeria: 'Africa',
  Egypt: 'Africa',
};

export function getRegionFromCountry(country) {
  if (!country || typeof country !== 'string') return null;
  const trimmed = country.trim();
  return COUNTRY_TO_REGION[trimmed] || null;
}

/**
 * Format a date string or timestamp to relative time and short date.
 * Returns { relativeKey, relativeParams, date } for i18n: t(relativeKey, relativeParams).
 */
export function formatRelativeTime(isoString) {
  if (!isoString) return { relativeKey: null, relativeParams: {}, date: '—' };
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return { relativeKey: null, relativeParams: {}, date: '—' };
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  let relativeKey = null;
  const relativeParams = {};
  if (diffMins < 1) relativeKey = 'timeJustNow';
  else if (diffMins < 60) {
    relativeKey = 'timeMinsAgo';
    relativeParams.count = diffMins;
  } else if (diffHours < 24) {
    relativeKey = 'timeHoursAgo';
    relativeParams.count = diffHours;
  } else if (diffDays === 1) {
    relativeKey = 'timeYesterday';
  } else if (diffDays < 7) {
    relativeKey = 'timeDaysAgo';
    relativeParams.count = diffDays;
  }

  const shortDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return { relativeKey, relativeParams, date: shortDate };
}

const INVALID_WEBSITE_VALUES = new Set(['false', 'true', 'null', 'undefined', 'none', 'n/a', 'na']);

/** Normalize supplier tier to "1" | "2" | "3" or "". */
export function normalizeSupplierTier(tier) {
  if (tier == null || tier === '') return '';
  const raw = String(tier).trim().toLowerCase();
  const tierWord = raw.match(/^tier\s*([123])$/);
  if (tierWord) return tierWord[1];
  if (raw === '1' || raw === '2' || raw === '3') return raw;
  const digit = raw.match(/^[123]$/);
  return digit ? digit[0] : '';
}

/** True when website is a real URL, not a placeholder like "false". */
export function isValidCompanyWebsite(website) {
  if (website == null || website === '') return false;
  const w = String(website).trim();
  if (!w || INVALID_WEBSITE_VALUES.has(w.toLowerCase())) return false;
  try {
    const url = w.startsWith('http') ? w : `https://${w}`;
    const parsed = new URL(url);
    return Boolean(parsed.hostname && parsed.hostname.includes('.'));
  } catch {
    return false;
  }
}

export function formatCompanyWebsiteHref(website) {
  if (!isValidCompanyWebsite(website)) return null;
  const w = String(website).trim();
  return w.startsWith('http') ? w : `https://${w}`;
}

/** Display risk score in header; returns null when no analysis exists. */
export function formatOverallRiskDisplay(overallRiskScore, riskAnalysisData) {
  const hasAnalysis = Boolean(riskAnalysisData);
  const raw = riskAnalysisData?.overallRiskScore ?? overallRiskScore;
  if (!hasAnalysis && (raw == null || raw === 0)) return null;
  const n = Number(raw);
  if (Number.isNaN(n)) return null;
  return n <= 10 ? String(n) : (n / 10).toFixed(1);
}

export function displayOrDash(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/** Risk tier from score 0-100: high (>=60), medium (40-59), low (<40) */
export function getRiskTier(score) {
  if (score == null || score === '') return 'low';
  const s = Number(score);
  if (s >= 60) return 'high';
  if (s >= 40) return 'medium';
  return 'low';
}

/** Company size type from employeeCount for display */
export function getCompanySizeType(employeeCount) {
  if (employeeCount == null || employeeCount === 0) return null;
  const n = Number(employeeCount);
  if (n >= 10000) return 'large';
  if (n >= 500) return 'medium';
  return 'small';
}

/** Industry badge colors for table/card display. Returns { bg, text }. */
export function getIndustryBadgeStyle(industry) {
  const key = (industry || '').toLowerCase();
  if (key.includes('tech') || key.includes('software')) return { bg: colors.info[100], text: colors.info[700] };
  if (key.includes('logistic') || key.includes('transport')) return { bg: colors.success[100], text: colors.success[700] };
  if (key.includes('manufactur') || key.includes('industrial')) return { bg: colors.info[50], text: colors.info[600] };
  if (key.includes('ship') || key.includes('maritime')) return { bg: colors.success[50], text: colors.success[600] };
  return { bg: colors.secondary[100], text: colors.secondary[700] };
}
